// FILE: src/components/LearnerDashboardEmpty.js
//
// Phase A's only "learner dashboard" surface — a portal overlay shown when a
// signed-in learner picks "Your Journey" from the header menu. Pre-readiness,
// pre-history, pre-plan: it's the calm orienting screen described in §3 of
// the design doc.
//
// Voice: calm-mentor coaching. "Let's set a baseline" — never "you must…",
// never streak/engagement framing.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './LearnerDashboardEmpty.module.css';
import CoachLayout from '../coach/CoachLayout';
import { getExamConfig, getActiveCertIds } from '../../config/exams';

// Phase A pre-selects PMP for the baseline session. Phase D will read
// profile.target_exam_id and pivot to the learner's actual target cert.
const BASELINE_EXAM_ID = 'pmp';
const ACTIVE_CERTS = getActiveCertIds();

/**
 * Calm orienting dialog inviting the learner to set a baseline.
 *
 * Two modes:
 *  • Cert-aware (caller passes `onStartCert` + `baselines`): renders a
 *    "Set a <cert> baseline" button for EACH active cert that has no baseline
 *    yet — one button when only the other track remains (e.g. PMP done, offer
 *    CPMAI), both buttons when neither is set. Used by the dashboard / readiness
 *    track switcher and the no-baseline dashboard state.
 *  • Legacy (only `onStartFirstSession`): single PMP "start practice" button.
 *    Preserved for the LearnerJourney portal surface.
 */
export default function LearnerDashboardEmpty({
  isOpen,
  onClose,
  onStartFirstSession,
  onStartCert,
  baselines = {},
  displayName,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const greeting = displayName ? `Welcome, ${displayName}.` : 'Welcome.';

  const certAware = typeof onStartCert === 'function';

  // Read the live practice config so CTA copy (counts/duration) stays accurate
  // if the per-cert defaults are tuned in src/config/exams.js.
  function certCta(certId) {
    const cfg = getExamConfig(certId);
    const label = cfg?.displayName || certId.toUpperCase();
    const q = cfg?.practiceExam?.defaultQuestions;
    const min = cfg?.practiceExam?.durationMinutes;
    return {
      certId,
      text: `Set a ${label} baseline  →`,
      subtitle: q && min ? `${q} questions · about ${min} minutes` : null,
      onClick: () => onStartCert(certId),
      testId: `learner-dashboard-set-baseline-${certId}`,
    };
  }

  let ctas;
  if (certAware) {
    // One "Set a <cert> baseline" button per active cert without a baseline:
    // both when neither is set, just the remaining track once one is done.
    const needing = ACTIVE_CERTS.filter((c) => !baselines[c]);
    ctas = (needing.length ? needing : ACTIVE_CERTS).map(certCta);
  } else {
    // Legacy single-PMP path (LearnerJourney portal).
    const cfg = getExamConfig(BASELINE_EXAM_ID);
    const q = cfg?.practiceExam?.defaultQuestions;
    const min = cfg?.practiceExam?.durationMinutes;
    ctas = [{
      certId: BASELINE_EXAM_ID,
      text: 'Start a PMP practice session  →',
      subtitle: q && min ? `${q} questions · about ${min} minutes` : null,
      onClick: () => (onStartFirstSession || onClose)?.(),
      testId: 'learner-dashboard-start-practice',
    }];
  }

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="learner-dashboard-empty-backdrop"
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="learner-journey-title"
      >
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close"
          onClick={() => onClose?.()}
          data-testid="learner-dashboard-empty-close"
        >
          ×
        </button>

        {/* Empty-state coaching moment — PM Pixel with a notepad sits beside
            the invitation (the reusable "coach layout"): larger, side-by-side
            with the heading + CTA, reading as part of the message rather than
            a decorative image. Secondary blocks below stay full-width. */}
        <CoachLayout pose="notepad" heightPx={220}>
          <p className={styles.eyebrow}>Your Certification Journey</p>
          <h2 id="learner-journey-title" className={styles.title}>
            {greeting} Let’s set a baseline.
          </h2>

          <p className={styles.lead}>
            Take a short practice session so we can calibrate your readiness.
            You can always step away — your progress saves automatically.
          </p>

          <div className={styles.ctaRow}>
            {ctas.map((c) => (
              <div className={styles.ctaItem} key={c.certId}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={c.onClick}
                  data-testid={c.testId}
                >
                  {c.text}
                </button>
                {c.subtitle && (
                  <p className={styles.ctaSubtitle}>{c.subtitle}</p>
                )}
              </div>
            ))}
          </div>
        </CoachLayout>

        <div className={styles.previewBlock}>
          <p className={styles.previewIntro}>As you progress, you’ll see:</p>
          <ul className={styles.previewList}>
            <li>Your readiness status</li>
            <li>A confidence forecast for your target exam date</li>
            <li>Milestones you’ve earned along the way</li>
            <li>A single recommended next step each day</li>
          </ul>
        </div>

        <div className={styles.optionalBlock}>
          <p className={styles.optionalLabel}>— Optional —</p>
          <p className={styles.optionalCopy}>
            Set a target exam date now to start a study plan.
            <span className={styles.optionalNote}> (Available once your study plan ships in a later release.)</span>
          </p>
        </div>

        <p className={styles.footnote}>
          You’re not behind. This is just the beginning of the calibration.
        </p>
      </div>
    </div>,
    document.body,
  );
}
